import { useLocalStorage } from '@vueuse/core';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, toValue, watch } from 'vue';

import { usePartitionedStorageKey } from '@rygen/rygen-ui/composables';
import { Module } from '@rygen/rygen-ui/model';

import {
  DomainBrandingRefreshPayload,
  Order,
  RecentlyViewedResult,
  Shipment,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

const MAX_RECENTLY_VIEWED_SIZE = 10;

export interface UseRecentlyViewedResponse {
  onDomainBrandingRefresh: (payload: DomainBrandingRefreshPayload) => void;
  recentlyViewed: ComputedRef<RecentlyViewedResult[]>;
}

export const useRecentlyViewed = (
  page?: MaybeRefOrGetter<Order | Shipment>,
): UseRecentlyViewedResponse => {
  const { currentUser, selectedDomain, selectedDomainBranding } =
    useCurrentUserStore();
  const { storageKey } = usePartitionedStorageKey('recentlyViewed', {
    module: Module.TMS,
    currentUser,
  });

  const _recentlyViewed = useLocalStorage(storageKey, [], {
    serializer: {
      read: (val: string) => {
        const results = JSON.parse(val) as Partial<RecentlyViewedResult>[];
        return results.map((r) => new RecentlyViewedResult(r));
      },
      write: (val: RecentlyViewedResult[]) => JSON.stringify(val),
    },
  });

  const recentlyViewed = computed(() => _recentlyViewed.value);

  const addRecent = (recent: RecentlyViewedResult) => {
    const duplicateIndex = _recentlyViewed.value.findIndex(
      (item) => item.id === recent.id && item.type === recent.type,
    );

    if (duplicateIndex >= 0) {
      _recentlyViewed.value.splice(duplicateIndex, 1);
    }

    _recentlyViewed.value.unshift(recent);
    if (_recentlyViewed.value.length > MAX_RECENTLY_VIEWED_SIZE) {
      _recentlyViewed.value.pop();
    }
  };

  const createRecentFromCurrentPage = (
    page: Order | Shipment,
  ): RecentlyViewedResult => {
    const recent = new RecentlyViewedResult({
      id: page.id,
      type: page instanceof Order ? 'ORDER' : 'SHIPMENT',
      requestedAppointmentEarliest: page.origin?.requestedAppointmentEarliest,
      requestedAppointmentLatest: page.origin?.requestedAppointmentLatest,
      currentUser: currentUser.value,
      selectedDomain: page.owner ?? selectedDomain.value,
      selectedDomainBranding: page.owner
        ? undefined
        : selectedDomainBranding.value,
    });

    if (page instanceof Shipment) {
      recent.scac = page.scac;
      recent.scheduledAppointmentEarliest =
        page.origin?.scheduledAppointmentEarliest;
      recent.scheduledAppointmentLatest =
        page.origin?.scheduledAppointmentLatest;
      recent.expectedArrival = page.origin?.expectedArrival;
      recent.actualArrival = page.origin?.actualArrival;
    }

    return recent;
  };

  const addCurrentlyViewedPage = (page: Order | Shipment) => {
    const recentlyViewedResult = createRecentFromCurrentPage(page);
    addRecent(recentlyViewedResult);
  };

  const onDomainBrandingRefresh = ({
    result,
    domainBranding,
  }: DomainBrandingRefreshPayload) => {
    _recentlyViewed.value = recentlyViewed.value.map((r) => {
      if (r === result) {
        r.selectedDomainBranding = domainBranding;
      }

      return r;
    });
  };

  watch(
    () => toValue(page),
    (newVal) => {
      if (newVal) {
        addCurrentlyViewedPage(newVal);
      }
    },
    { immediate: true },
  );

  return {
    onDomainBrandingRefresh,
    recentlyViewed,
  };
};
